import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useActivateTask,
  useApprove,
  useAppeals,
  useCoClaims,
  useMarkNotDone,
  useReject,
  useResolveAppeal,
  useResolveCoClaim,
  useRestore,
  useSettings,
  useTasks,
  useUsers,
  useWarn,
  useWeek,
} from '../api/hooks.ts'
import { Button, Card, ErrorNote, Screen, SectionTitle, Spinner } from '../components/ui.tsx'
import { ActivateDialog } from '../components/ActivateDialog.tsx'
import { PhotoThumb } from '../components/PhotoThumb.tsx'
import { PromptDialog } from '../components/PromptDialog.tsx'
import { TaskRow } from '../components/TaskRow.tsx'
import { dayLabel, eur, shortDate, weekLabel } from '../lib/format.ts'
import type { TaskDefinition, User } from '../api/types.ts'

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function AdminHome() {
  const users = useUsers()
  const children = useMemo(() => (users.data ?? []).filter((u) => u.role === 'CHILD'), [users.data])

  if (users.isLoading) return <Spinner />
  if (users.error) return <Screen><ErrorNote error={users.error} /></Screen>

  return (
    <Screen>
      <header className="mb-4">
        <h1 className="text-xl font-bold tracking-tight">Esta semana</h1>
      </header>
      <AppealsInbox />
      <CoClaimsInbox />
      <OnDemandTasks />
      {children.map((child) => (
        <ChildSection key={child.id} child={child} />
      ))}
    </Screen>
  )
}

function ChildSection({ child }: { child: User }) {
  const week = useWeek(child.id)
  const settings = useSettings()
  const warn = useWarn()
  const notDone = useMarkNotDone()
  const restore = useRestore()
  const approve = useApprove()
  const reject = useReject()

  // Un único cuadro para los dos motivos que se piden aquí, en vez del prompt
  // del sistema, que en iOS recorta el texto y no deja escribir en varias líneas.
  const [asking, setAsking] = useState<
    { kind: 'reject' | 'restore'; id: string; title: string } | null
  >(null)

  const today = todayIso()

  if (week.isLoading || settings.isLoading) return <Card className="mb-4">Cargando…</Card>
  if (week.error) return <div className="mb-4"><ErrorNote error={week.error} /></div>
  if (!week.data || !settings.data) return null

  const { breakdown, week: w, instances } = week.data
  const threshold = settings.data.warningThreshold
  const closed = w.status !== 'OPEN'

  const todayTasks = instances.filter((i) => i.kind === 'DEDUCTIBLE' && i.dueDate === today)
  const failed = instances.filter((i) => i.kind === 'DEDUCTIBLE' && i.status === 'NOT_DONE')
  const toApprove = instances.filter((i) => i.kind === 'BONUS' && i.status === 'CLAIMED')

  const ratio = w.baseAmountCents > 0 ? breakdown.payoutCents / w.baseAmountCents : 1
  const barColor =
    ratio >= 0.95 ? 'bg-[var(--color-good)]' : ratio >= 0.6 ? 'bg-[var(--color-warn)]' : 'bg-[var(--color-bad)]'

  const anyError = warn.error ?? notDone.error ?? restore.error ?? approve.error ?? reject.error

  return (
    <section className="mb-8">
      <Card>
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">{child.name}</h2>
          <span className="text-xl font-bold tabular-nums">{eur(breakdown.payoutCents)}</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-line)]">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {weekLabel(w.weekStart, w.weekEnd)}
          {breakdown.deductionsAppliedCents > 0 && ` · −${eur(breakdown.deductionsAppliedCents)}`}
          {breakdown.bonusAppliedCents > 0 && ` · +${eur(breakdown.bonusAppliedCents)} bonus`}
        </p>
        {/* Informacion operativa: si el tope se ha agotado, marcar mas tareas ya
            no tiene efecto economico y solo quedan los avisos y la conversacion. */}
        {breakdown.deductionCapReached && (
          <p className="mt-2 rounded-lg bg-[var(--color-warn)]/15 px-3 py-1.5 text-xs text-[var(--color-warn)]">
            Tope de deducción alcanzado: marcar más tareas ya no descuenta nada
          </p>
        )}
        <Link
          to={`/semana/${w.id}/desglose`}
          className="mt-3 inline-block text-sm font-medium text-[var(--color-brand)]"
        >
          Ver desglose →
        </Link>
      </Card>

      {anyError != null && <div className="mt-2"><ErrorNote error={anyError} /></div>}

      {toApprove.length > 0 && (
        <>
          <SectionTitle>💰 Por aprobar ({toApprove.length})</SectionTitle>
          <Card className="flex flex-col gap-3">
            {toApprove.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {i.hasPhoto && <PhotoThumb instanceId={i.id} alt={i.title} />}
                  <div className="min-w-0">
                    <p className="text-[15px] break-words">{i.title}</p>
                    <p className="text-xs text-[var(--color-good)]">+{eur(i.valueCents)}</p>
                    {i.requiresPhoto && !i.hasPhoto && (
                      <p className="text-xs text-[var(--color-warn)]">
                        Sin foto: no se puede aprobar todavía
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => approve.mutate({ id: i.id })}
                    // El servidor lo rechaza igualmente; deshabilitarlo evita el
                    // error innecesario y explica por qué.
                    disabled={approve.isPending || closed || (i.requiresPhoto && !i.hasPhoto)}
                  >
                    Aprobar
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={reject.isPending || closed}
                    onClick={() => setAsking({ kind: 'reject', id: i.id, title: i.title })}
                  >
                    Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      <SectionTitle>Hoy · {dayLabel(today)}</SectionTitle>
      <Card>
        {todayTasks.length === 0 ? (
          <p className="py-2 text-sm text-[var(--color-muted)]">Sin tareas hoy.</p>
        ) : (
          todayTasks.map((i) => (
            <TaskRow
              key={i.id}
              instance={i}
              warningThreshold={threshold}
              right={
                closed ? null : i.status === 'NOT_DONE' ? (
                  <button
                    className="ml-1 shrink-0 rounded-lg px-2 py-2 text-xs font-semibold text-[var(--color-brand)]"
                    onClick={() => setAsking({ kind: 'restore', id: i.id, title: i.title })}
                  >
                    Deshacer
                  </button>
                ) : (
                  // Botones visibles en vez de deslizar: es igualmente un toque,
                  // se descubren solos y no dependen de acertar un gesto.
                  <span className="ml-1 flex shrink-0 gap-1">
                    <button
                      aria-label={`Enviar aviso por ${i.title}`}
                      className="h-10 w-10 rounded-lg border border-[var(--color-line)] text-[var(--color-warn)]"
                      disabled={warn.isPending}
                      onClick={() => warn.mutate({ id: i.id })}
                    >
                      ⚠
                    </button>
                    <button
                      aria-label={`Marcar ${i.title} como no hecha`}
                      className="h-10 w-10 rounded-lg border border-[var(--color-line)] text-[var(--color-bad)]"
                      disabled={notDone.isPending}
                      onClick={() => notDone.mutate({ id: i.id })}
                    >
                      ✕
                    </button>
                  </span>
                )
              }
            />
          ))
        )}
      </Card>

      {failed.length > 0 && (
        <>
          <SectionTitle>No hechas esta semana ({failed.length})</SectionTitle>
          <Card>
            {failed.map((i) => (
              <TaskRow key={i.id} instance={i} warningThreshold={threshold} />
            ))}
          </Card>
        </>
      )}

      <PromptDialog
        open={asking !== null}
        title={asking?.kind === 'reject' ? 'Rechazar el bonus' : 'Deshacer la marca'}
        description={
          asking
            ? asking.kind === 'reject'
              ? `Se le explicará a ${child.name} por qué no se le abona «${asking.title}».`
              : `«${asking.title}» volverá a contar como hecha y se devolverá el descuento.`
            : undefined
        }
        placeholder="Motivo…"
        confirmLabel={asking?.kind === 'reject' ? 'Rechazar' : 'Deshacer'}
        tone={asking?.kind === 'reject' ? 'danger' : 'primary'}
        busy={reject.isPending || restore.isPending}
        onCancel={() => setAsking(null)}
        onConfirm={(reason) => {
          if (!asking) return
          const mutation = asking.kind === 'reject' ? reject : restore
          mutation.mutate(
            { id: asking.id, body: { reason } },
            { onSuccess: () => setAsking(null) },
          )
        }}
      />
    </section>
  )
}

/**
 * Bandeja de alegaciones.
 *
 * Va arriba del todo y fuera de la ficha de cada hijo: es lo único que espera
 * respuesta de una persona concreta, y dejarla enterrada entre las tareas del
 * día haría que se quedara sin contestar.
 */
function AppealsInbox() {
  const appeals = useAppeals({ onlyOpen: true })
  const resolve = useResolveAppeal()
  const [deciding, setDeciding] = useState<{ id: string; accepted: boolean; title: string } | null>(
    null,
  )

  const open = appeals.data ?? []
  if (open.length === 0) return null

  return (
    <section className="mb-8">
      <SectionTitle>⚖️ Alegaciones ({open.length})</SectionTitle>
      {resolve.error != null && <ErrorNote error={resolve.error} />}
      <Card className="flex flex-col gap-4">
        {open.map((appeal) => (
          <div key={appeal.id} className="border-b border-[var(--color-line)] pb-4 last:border-b-0 last:pb-0">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[15px] font-medium">{appeal.taskTitle}</p>
              <span className="shrink-0 text-sm text-[var(--color-bad)]">
                −{eur(appeal.valueCents)}
              </span>
            </div>
            <p className="text-xs text-[var(--color-muted)]">
              {appeal.child.name} · {shortDate(appeal.dueDate)}
            </p>
            <blockquote className="mt-2 rounded-xl bg-[var(--color-canvas)] px-3 py-2 text-sm">
              «{appeal.message}»
            </blockquote>
            <div className="mt-3 flex gap-2">
              <Button
                className="flex-1"
                disabled={resolve.isPending}
                onClick={() =>
                  setDeciding({ id: appeal.id, accepted: true, title: appeal.taskTitle })
                }
              >
                Le doy la razón
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                disabled={resolve.isPending}
                onClick={() =>
                  setDeciding({ id: appeal.id, accepted: false, title: appeal.taskTitle })
                }
              >
                Mantengo el descuento
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <PromptDialog
        open={deciding !== null}
        title={deciding?.accepted ? 'Devolver el descuento' : 'Mantener el descuento'}
        description={
          deciding
            ? deciding.accepted
              ? `Se le devolverá lo descontado por «${deciding.title}» y quedará registrado.`
              : 'Explícale por qué se mantiene: lo verá en su móvil.'
            : undefined
        }
        placeholder="Explicación…"
        confirmLabel={deciding?.accepted ? 'Devolver' : 'Mantener'}
        tone={deciding?.accepted ? 'primary' : 'danger'}
        busy={resolve.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={(note) => {
          if (!deciding) return
          resolve.mutate(
            { id: deciding.id, body: { accepted: deciding.accepted, note } },
            { onSuccess: () => setDeciding(null) },
          )
        }}
      />
    </section>
  )
}

/**
 * Tareas a demanda.
 *
 * Hay trabajo que no tiene calendario: la ropa se tiende el día que hay
 * lavadora. Estas tareas existen configuradas pero no aparecen en el móvil de
 * nadie hasta que se pulsa aquí, que es cuando de verdad hacen falta.
 */
function OnDemandTasks() {
  const tasks = useTasks()
  const users = useUsers()
  const activate = useActivateTask()
  const [opening, setOpening] = useState<TaskDefinition | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const onDemand = (tasks.data ?? []).filter((t) => t.recurrence === 'ON_DEMAND')
  if (onDemand.length === 0) return null

  return (
    <section className="mb-8">
      <SectionTitle>Activar ahora</SectionTitle>
      {activate.error != null && <ErrorNote error={activate.error} />}
      <Card className="flex flex-col gap-3">
        {onDemand.map((task) => (
          <div key={task.id} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[15px] break-words">{task.title}</p>
              <p className="text-xs text-[var(--color-muted)]">
                {task.kind === 'BONUS'
                  ? `Bonus · +${eur(task.bonusAmountCents ?? 0)}`
                  : 'Obligatoria · se descuenta si no se hace'}
                {done === task.id && ' · puesta hoy'}
              </p>
            </div>
            <Button
              className="shrink-0"
              variant={done === task.id ? 'secondary' : 'primary'}
              disabled={activate.isPending}
              onClick={() => setOpening(task)}
            >
              {done === task.id ? 'Otra vez' : 'Activar'}
            </Button>
          </div>
        ))}
      </Card>

      <ActivateDialog
        open={opening !== null}
        task={opening}
        candidates={(users.data ?? []).filter((u) => u.role === 'CHILD')}
        busy={activate.isPending}
        onCancel={() => setOpening(null)}
        onConfirm={(activation) => {
          if (!opening) return
          const id = opening.id
          activate.mutate(
            { id, body: activation },
            {
              onSuccess: () => {
                setDone(id)
                setOpening(null)
              },
            },
          )
        }}
      />
    </section>
  )
}

/**
 * Peticiones de reparto de un bonus abierto (§6.10).
 *
 * Un bonus abierto se lo lleva quien lo reclama primero, pero a veces lo han
 * hecho los dos. Aquí se leen las dos versiones y se decide en un solo gesto:
 * entero para quien lo reclamó, o a medias. Si ya estaba aprobado, repartirlo
 * mueve la mitad de una semana a la otra.
 */
function CoClaimsInbox() {
  const coClaims = useCoClaims({ onlyOpen: true })
  const resolve = useResolveCoClaim()
  const [deciding, setDeciding] = useState<
    { id: string; accepted: boolean; title: string; who: string } | null
  >(null)

  const open = coClaims.data ?? []
  if (open.length === 0) return null

  return (
    <section className="mb-8">
      <SectionTitle>🤝 A medias ({open.length})</SectionTitle>
      {resolve.error != null && <ErrorNote error={resolve.error} />}
      <Card className="flex flex-col gap-4">
        {open.map((coClaim) => {
          const half = Math.floor(coClaim.valueCents / 2)
          const blocked = coClaim.instanceStatus === 'CLAIMED' && coClaim.requiresPhoto && !coClaim.hasPhoto
          return (
            <div
              key={coClaim.id}
              className="border-b border-[var(--color-line)] pb-4 last:border-b-0 last:pb-0"
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[15px] font-medium break-words">{coClaim.taskTitle}</p>
                <span className="shrink-0 text-sm text-[var(--color-good)]">
                  +{eur(coClaim.valueCents)}
                </span>
              </div>
              <p className="text-xs text-[var(--color-muted)]">
                La reclamó {coClaim.claimer?.name ?? '?'} ·{' '}
                {coClaim.instanceStatus === 'APPROVED' ? 'ya aprobada' : 'pendiente de aprobar'}
              </p>
              <blockquote className="mt-2 rounded-xl bg-[var(--color-canvas)] px-3 py-2 text-sm">
                <strong>{coClaim.child.name}:</strong> «{coClaim.message}»
              </blockquote>
              {blocked && (
                <p className="mt-2 text-xs text-[var(--color-warn)]">
                  Falta la foto: no se puede aprobar todavía
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  className="flex-1"
                  disabled={resolve.isPending || blocked}
                  onClick={() =>
                    setDeciding({
                      id: coClaim.id,
                      accepted: true,
                      title: coClaim.taskTitle,
                      who: `${eur(coClaim.valueCents - half)} para ${coClaim.claimer?.name ?? '?'} y ${eur(half)} para ${coClaim.child.name}`,
                    })
                  }
                >
                  A medias
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  disabled={resolve.isPending || blocked}
                  onClick={() =>
                    setDeciding({
                      id: coClaim.id,
                      accepted: false,
                      title: coClaim.taskTitle,
                      who: `${eur(coClaim.valueCents)} enteros para ${coClaim.claimer?.name ?? '?'}`,
                    })
                  }
                >
                  Entero para {coClaim.claimer?.name ?? 'quien la reclamó'}
                </Button>
              </div>
            </div>
          )
        })}
      </Card>

      <PromptDialog
        open={deciding !== null}
        title={deciding?.accepted ? 'Repartir a medias' : 'Dejarlo entero'}
        description={deciding ? `${deciding.who}. Lo verán los dos en su móvil.` : undefined}
        placeholder="Explica la decisión…"
        confirmLabel={deciding?.accepted ? 'Repartir' : 'Dejarlo entero'}
        busy={resolve.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={(note) => {
          if (!deciding) return
          resolve.mutate(
            { id: deciding.id, body: { accepted: deciding.accepted, note } },
            { onSuccess: () => setDeciding(null) },
          )
        }}
      />
    </section>
  )
}
